import { Button, Input } from "@heroui/react";
import { clockOf, minuteOf } from "../../lib/conditions";
import { problemText } from "../../lib/profilesProblems";
import {
  CONDITION_KINDS,
  newCondition,
  splitPhrase,
  type Slot,
} from "../../lib/profilesRules";
import type {
  ProfileProblem,
  RuleCondition,
  RuleConditionKind,
} from "../../lib/types";
import { Icon } from "../../ui/icons";
import { useStaffText } from "../../ui/StaffText";
import { Pick, type PickOption } from "../setupKit";

/** A problem with its path cut down to the part under the block showing it. */
export type Pin = { problem: ProfileProblem; field: string };

/** The pins under `prefix` (`rules[2]`), their fields made relative to it. */
export function pinsUnder(pins: readonly Pin[], prefix: string): Pin[] {
  const out: Pin[] = [];
  for (const pin of pins) {
    if (pin.field === prefix) out.push({ ...pin, field: "" });
    else if (pin.field.startsWith(`${prefix}.`))
      out.push({ ...pin, field: pin.field.slice(prefix.length + 1) });
  }
  return out;
}

/** Problem lines for the pins whose field passes `at`. */
export function PinLines({
  pins,
  at,
}: {
  pins: readonly Pin[];
  at: (field: string) => boolean;
}) {
  const { lang } = useStaffText();
  const shown = pins.filter((pin) => at(pin.field));
  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((pin, i) => (
        <p
          key={i}
          className={
            pin.problem.warning ? "pf-wp__warn" : "field-error pf-wp__error"
          }
        >
          {problemText(pin.problem, lang)}
        </p>
      ))}
    </>
  );
}

const N_BOUNDS: Partial<Record<RuleConditionKind, [number, number]>> = {
  wait_over: [1, 240],
  over_service_level: [0, 240],
  appointment_late: [0, 240],
  visits_count: [0, 999],
  called_count: [0, 999],
};

/** One AND'd condition: its kind, then its sentence with a control in each slot. */
export function ConditionRow({
  condition: c,
  queues,
  pins,
  onChange,
  onRemove,
  readOnly,
}: {
  condition: RuleCondition;
  /** The profile's queues, for the queue slot. */
  queues: PickOption[];
  pins: Pin[];
  onChange: (next: RuleCondition) => void;
  onRemove: () => void;
  readOnly: boolean;
}) {
  const { say } = useStaffText();
  const set = (patch: Partial<RuleCondition>) => onChange({ ...c, ...patch });
  const bounds = N_BOUNDS[c.kind] ?? [0, 999];

  const control = (slot: Slot) => {
    switch (slot) {
      case "queue":
        return (
          <Pick
            size="sm"
            className="pf-cond__queue"
            label={say("profiles.queue")}
            value={c.queueId ?? ""}
            options={queues}
            disabled={readOnly}
            onChange={(queueId) => set({ queueId })}
          />
        );
      case "op":
        return (
          <Pick
            size="sm"
            label={say("profiles.wp.compare")}
            value={c.op ?? ""}
            disabled={readOnly}
            options={(["lt", "gt", "eq"] as const).map((op) => ({
              value: op,
              label: say(`profiles.op.${op}`),
            }))}
            onChange={(op) => set({ op: op as RuleCondition["op"] })}
          />
        );
      case "scope":
        return (
          <Pick
            size="sm"
            label={say("profiles.wp.scope")}
            value={c.scope ?? ""}
            disabled={readOnly}
            options={(["user", "profile", "branch"] as const).map((s) => ({
              value: s,
              label: say(`profiles.scope.${s}`),
            }))}
            onChange={(scope) =>
              set({ scope: scope as RuleCondition["scope"] })
            }
          />
        );
      case "n":
        return (
          <Input
            className="pf-cond__n"
            type="number"
            inputMode="numeric"
            aria-label={say("profiles.wp.number")}
            min={bounds[0]}
            max={bounds[1]}
            value={c.n}
            disabled={readOnly}
            aria-invalid={pins.some((p) => p.field === "n") || undefined}
            onChange={(e) => set({ n: Number(e.target.value) || 0 })}
          />
        );
      case "from":
      case "until":
        return (
          <Input
            className="pf-cond__time"
            type="time"
            aria-label={say(slot === "from" ? "common.from" : "common.to")}
            value={clockOf(c[slot])}
            disabled={readOnly}
            onChange={(e) => set({ [slot]: minuteOf(e.target.value) })}
          />
        );
    }
  };

  return (
    <li className="pf-cond">
      <div className="pf-cond__line">
        <Pick
          size="sm"
          className="pf-cond__kind"
          label={say("profiles.wp.condition")}
          value={c.kind}
          disabled={readOnly}
          options={CONDITION_KINDS.map((kind) => ({
            value: kind,
            label: say(`profiles.condKind.${kind}`),
          }))}
          // A new kind starts from its own example: ten minutes is not ten visits.
          onChange={(kind) =>
            onChange(
              newCondition(
                kind as RuleConditionKind,
                c.queueId || String(queues[0]?.value ?? ""),
              ),
            )
          }
        />
        <span className="pf-cond__sentence">
          {splitPhrase(say(`profiles.cond.${c.kind}`)).map((part, i) =>
            typeof part === "string" ? (
              <span key={i} className="pf-cond__word">
                {part}
              </span>
            ) : (
              <span key={i} className="pf-cond__slot">
                {control(part.slot)}
              </span>
            ),
          )}
        </span>
        {!readOnly && (
          <Button
            isIconOnly
            variant="ghost"
            size="sm"
            className="pf-cond__remove"
            aria-label={say("common.remove")}
            onPress={onRemove}
          >
            <Icon name="close" size={16} />
          </Button>
        )}
      </div>
      <PinLines pins={pins} at={() => true} />
    </li>
  );
}
